import bluebird from 'bluebird'
import {
  CreateReducer,
  CreateAction,
} from '../utils/store'

import {
  NetworkThunk,
} from '../utils/network'

import {
  setHTTPToken,
  unsetHTTPToken,
  getHTTPTokenHeaders,
  handlers,
} from '../utils/api'

import routerActions from './router'
import snackbarActions from './snackbar'

import {
  REFRESH_TOKEN_DELAY,
  ROUTE_LOGGED_IN,
  ROUTE_LOGGED_OUT,
  IS_DEVELOPMENT,
} from '../../settings'

import {
  AuthState,
  ReducerHandler,
  ThunkHandler,
} from '../../types/store'

import {
  IQuery_Id,
} from '../../types/utils'

import {
  IQuery_User_Login,
  IQuery_User_UpdatePassword,
  IData_UserToken,
  IQuery_User_ResetPassword,
} from '../../types/user'

import authSelectors from '../selectors/auth'

const prefix = 'auth'

/*

  initial state

*/
const initialState: AuthState = {
  loaded: false,
  token: '',
  data: null,
}

/*

  utils

*/
let refreshTokenLoop: any = null

/*

  reducers

*/
const setUser: ReducerHandler<AuthState, any> = (state, action) => {
  state.loaded = true
  state.data = action.payload
}

const setToken: ReducerHandler<AuthState, string> = (state, action) => {
  const token = action.payload
  if(token) {
    setHTTPToken(token)
  }
  else {
    unsetHTTPToken()
  }
  localStorage.setItem('token', token)
  state.token = token
}

/*

  thunks

*/
const authenticate: ThunkHandler<string, void> = (token) => async (dispatch, getState) => {
  // when impersonating - we might be running this again after the initial load
  await dispatch(actions.stopTokenLoop())

  let user: any = null
  token = token || localStorage.getItem('token') || ''
  if(token) {
    try {
      user = await handlers.get<any>('/auth/status', {
        headers: getHTTPTokenHeaders(token),
      })
    } catch(e) {

    }
  }
  if(user && user.id) {
    dispatch(actions.setToken(token))
    dispatch(startTokenLoop())
    dispatch(actions.setUser(user))
  }
  else {
    // don't clear the token whilst we are reloading
    if(!IS_DEVELOPMENT) {
      dispatch(actions.setToken(''))
    }
    dispatch(actions.setUser(null))
  }
}

const refreshToken: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  try {
    const { token } = await handlers.post<void, IData_UserToken>('/auth/token')
    dispatch(actions.setToken(token))
  } catch(e) {
    // dispatch(actions.setToken(''))
    // dispatch(snackbarActions.setError(`you have been logged out`))
    // dispatch(logout())
  }
}

const startTokenLoop: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  if(refreshTokenLoop) {
    clearInterval(refreshTokenLoop)
  }
  refreshTokenLoop = setInterval(() => {
    dispatch(refreshToken())
  }, REFRESH_TOKEN_DELAY)
}

const stopTokenLoop: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  if(refreshTokenLoop) {
    clearInterval(refreshTokenLoop)
    refreshTokenLoop = null
  }
}

const login: ThunkHandler<IQuery_User_Login, void> = (payload) => async (dispatch, getState) => {
  const {
    token,
  } = await handlers.post<IQuery_User_Login, IData_UserToken>('/auth/login', payload)
  await dispatch(authenticate(token))
  dispatch(routerActions.navigateTo(ROUTE_LOGGED_IN))
}

const logout: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  dispatch(actions.setToken(''))
  dispatch(actions.setUser(null))
  document.location.pathname = '/'
  await bluebird.delay(10000)
}

const resetPassword: ThunkHandler<IQuery_User_ResetPassword, void> = (payload) => async (dispatch, getState) => {
  await handlers.post<IQuery_User_ResetPassword, void>('/auth/password_reset', payload)
  dispatch(snackbarActions.setSuccess(`password reset request submitted - please check your email`))
}

const updatePassword: ThunkHandler<IQuery_User_UpdatePassword, void> = (payload) => async (dispatch, getState) => {
  const result = await handlers.put<IQuery_User_UpdatePassword, any>('/auth/password', payload)
  dispatch(actions.setUser(result))
  dispatch(snackbarActions.setSuccess(`password updated`))
  dispatch(routerActions.navigateTo(ROUTE_LOGGED_IN))
}

/*

  collections

*/
const reducers = {
  setUser,
  setToken,
}

export const actions = {
  setUser: CreateAction(prefix ,'setUser', setUser),
  setToken: CreateAction(prefix ,'setToken', setToken),
  authenticate,
  refreshToken,
  startTokenLoop,
  stopTokenLoop,
  login: NetworkThunk(prefix, 'login', login, {
    snackbar: true,
    globalLoading: {
      message: 'logging in...',
    },
  }),
  logout: NetworkThunk(prefix, 'logout', logout, {
    snackbar: true,
    globalLoading: {
      message: 'logging out...',
    },
  }),
  resetPassword: NetworkThunk(prefix, 'resetPassword', resetPassword, {
    snackbar: true,
    globalLoading: {
      message: 'requesting password reset...',
    },
  }),
  updatePassword,
}

export const reducer = CreateReducer<AuthState>({
  prefix,
  initialState,
  reducers,
})

export default actions